import assert from 'assert';
import React, { FC, useEffect, useRef, useState } from 'react';
import SignerSelection from '~/src/components/SignerSelection/SignerSelection';

import {
  extractNewAssignmentId,
  makeAssignDateNowBody,
  makeAssignSignatureOrDateBody,
  makePackagePdfs,
  makePackageSigners,
  makePageIdsByPageNumber,
  sanitizeSignature,
} from '~/src/components/SignerSelection/utils';
import {
  CURRENT_STEP,
  SIGNER_MISMATCH,
} from '~/src/components/SignaturePackage/constants';
import useMst from '~/src/hooks/useMst';
import {
  Assignee,
  UpdateElementPayload,
  Dated,
  SignaturePackage,
  WhoSigns,
} from '../models';

interface Props {
  signaturePackage: SignaturePackage.SignaturePackage;
  setValid: (valid: boolean, reason?: string) => void;
}

const SignerSelectionContainer: FC<Props> = ({
  signaturePackage,
  setValid,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSwapping, setIsSwapping] = useState(false);
  const swapAttempted = useRef(false);
  const { signatures, user, profile } = useMst((store) => {
    return {
      profile: store.user.profile,
      signatures: store.signatures,
      user: store.user,
    };
  });

  const currentUserEmail = user.currentOrganization?.email || '';
  const currentUserName = user.profile?.displayName || '';

  const hasRecipients = signaturePackage.recipients.length > 0;
  const signers = hasRecipients
    ? makePackageSigners(signaturePackage, currentUserEmail)
    : null;

  const needsSwap =
    hasRecipients &&
    !!currentUserEmail &&
    !signers &&
    signaturePackage.whoSigns !== WhoSigns.Others &&
    signaturePackage.packageStatus === 'Drafting';

  useEffect(() => {
    if (!needsSwap || swapAttempted.current) return;
    swapAttempted.current = true;

    const meRecipient = signaturePackage.recipients.find(
      (r) => r.signOrder === 0,
    );

    const swappedRecipients = [
      {
        id: meRecipient?.id ?? null,
        email: currentUserEmail,
        name: currentUserName,
        signOrder: 0,
      },
      ...signaturePackage.recipients
        .filter((r) => r.signOrder !== 0)
        .map(({ id, email, name, signOrder }) => ({
          id,
          email,
          name,
          signOrder,
        })),
    ];

    const swappedPackageData: SignaturePackage.LeanSignaturePackage = {
      id: signaturePackage.id,
      whoSigns: signaturePackage.whoSigns,
      recipients: swappedRecipients,
      customOrder: signaturePackage.customOrder,
      currentStep: CURRENT_STEP.CHOOSE_SIGNERS,
    };

    setIsSwapping(true);
    signatures
      .updateSignaturePackage('', swappedPackageData)
      .then(() => setIsSwapping(false))
      .catch((error: unknown) => {
        console.error('Failed to auto-swap signer recipient:', error);
        setIsSwapping(false);
      });
  }, [
    needsSwap,
    signaturePackage,
    signatures,
    currentUserEmail,
    currentUserName,
  ]);

  const signerMismatch = hasRecipients && !isSwapping && !signers;

  useEffect(() => {
    if (signerMismatch) {
      setValid(false, SIGNER_MISMATCH);
    }
  }, [signerMismatch, setValid]);

  if (!hasRecipients || isSwapping) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        Loading...
      </div>
    );
  }

  if (!signers) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center gap-4 p-8 text-center">
        <p className="text-lg font-semibold">Unable to sign this package</p>
        <p className="text-sm text-gray-600">
          You are signed in as{' '}
          <strong>{currentUserEmail || 'an unknown account'}</strong>, but this
          package&apos;s signer is a different user. You cannot sign on behalf
          of another user.
        </p>
      </div>
    );
  }

  const pdfs = makePackagePdfs(signaturePackage);

  const signNow = async (
    documentId: string,
    assignee: Assignee,
    imageParams: { xfdfSignature: string; width: number; height: number },
  ): Promise<number> => {
    assert(
      'me' in signers,
      'Me not found in signers when uploading current user signature!',
    );
    const pdf = signaturePackage.lawyawDocuments.find(
      (e) => e.id.toString() === documentId,
    )!;
    const pageIdsByPageNumber = makePageIdsByPageNumber(pdf.pages);
    const assignRequestBody = makeAssignSignatureOrDateBody(
      assignee,
      pageIdsByPageNumber,
    );
    /**
     * Any time we sign anywhere on the document, we want to be sure
     * that the signature we used becomes the default signature for
     * the user's profile. Much of the time this request isn't needed
     * because we're reusing the same signature as is on the user's
     * profile. So an optimization here would be to conditionally
     * fire this request only when the signature changes
     */
    setIsLoading(true);
    await user.profile?.uploadSignatureXfdf(imageParams.xfdfSignature);
    const updatedPackage = await signatures.uploadUserSignatureImageXfdf(
      {
        lawyawProjectId: signaturePackage.lawyawProjectId,
        fprint: signaturePackage.fprint,
      },
      assignee.recipient.id,
      imageParams,
      [assignRequestBody],
    );
    const id = extractNewAssignmentId(updatedPackage, documentId, assignee);
    setIsLoading(false);
    return id;
  };

  const upsertElements = async (
    documentId: string,
    elements: UpdateElementPayload[],
  ): Promise<number[]> => {
    const pdf = signaturePackage.lawyawDocuments.find(
      (e) => e.id.toString() === documentId,
    )!;
    const pageIdsByPageNumber = makePageIdsByPageNumber(pdf.pages);
    const elementsToUpsert = elements.map((element) =>
      makeAssignSignatureOrDateBody(element, pageIdsByPageNumber),
    );
    setIsLoading(true);
    if (elementsToUpsert.length > 0) {
      const newPackage = await signatures.assignSignatureField(
        {
          lawyawProjectId: signaturePackage.lawyawProjectId,
          fprint: signaturePackage.fprint,
        },
        elementsToUpsert,
      );
      setIsLoading(false);
      return elements.map((element) =>
        extractNewAssignmentId(newPackage, documentId, element),
      );
    }
    setIsLoading(false);
    return [];
  };

  const deleteAssignment = (assignmentId: number) => {
    signatures.deleteAssignedSignatureField(
      {
        lawyawProjectId: signaturePackage.lawyawProjectId,
        fprint: signaturePackage.fprint,
      },
      assignmentId,
    );
  };

  const assignDateNow = async (documentId: string, dated: Dated) => {
    const pdf = signaturePackage.lawyawDocuments.find(
      (e) => e.id.toString() === documentId,
    )!;
    const pageIdsByPageNumber = makePageIdsByPageNumber(pdf.pages);
    const requestBody = makeAssignDateNowBody(dated, pageIdsByPageNumber);
    setIsLoading(true);
    const newPackage = await signatures.assignSignatureField(
      {
        lawyawProjectId: signaturePackage.lawyawProjectId,
        fprint: signaturePackage.fprint,
      },
      requestBody,
    );
    setIsLoading(false);
    return extractNewAssignmentId(newPackage, documentId, dated);
  };

  return (
    <div className="flex flex-col grow">
      <SignerSelection
        signers={signers}
        onAssignFields={upsertElements}
        onDeleteAssignedField={deleteAssignment}
        onDateNow={assignDateNow}
        pdfs={pdfs}
        onSignNow={signNow}
        setValid={setValid}
        isLoading={isLoading}
        myCurrentSignature={
          profile && profile.xfdfSignature
            ? sanitizeSignature(profile.xfdfSignature)
            : null
        }
      />
    </div>
  );
};

export default SignerSelectionContainer;
